// src/components/PublicRatingBadge.jsx

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

// GET /api/public/note/:facilityId (PublicNote.ts) returns facilityType
// as the string values below (e.g. 'restaurant') - never the numeric
// 0-10/99 this used to be keyed by, which made every badge show "Other"
// regardless of the facility's real type. Numeric keys kept for any
// legacy rows.
const FACILITY_TYPE_KEYS = {
  restaurant: 'restaurant',
  gite: 'gite',
  bar: 'bar',
  hotel: 'hotel',
  museum: 'museum',
  shop: 'shop',
  pharmacy: 'pharmacy',
  bakery: 'bakery',
  activity: 'activity',
  camping: 'camping',
  fitness_center: 'gym',
  gym: 'gym',
  other: 'other',
  0: 'restaurant',
  1: 'gite',
  2: 'bar',
  3: 'hotel',
  4: 'museum',
  5: 'shop',
  6: 'pharmacy',
  7: 'bakery',
  8: 'activity',
  9: 'camping',
  10: 'gym',
  99: 'other',
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function Star({ fillPercent }) {
  const safeFill = clamp(fillPercent, 0, 100);

  return (
    <span
      aria-hidden="true"
      style={{
        position: 'relative',
        display: 'inline-block',
        width: '1em',
        height: '1em',
        fontSize: 'clamp(30px, 7vw, 52px)',
        lineHeight: 1,
      }}
    >
      <span
        style={{
          position: 'absolute',
          inset: 0,
          color: '#d5d9df',
        }}
      >
        ★
      </span>

      <span
        style={{
          position: 'absolute',
          inset: 0,
          width: `${safeFill}%`,
          overflow: 'hidden',
          color: '#f5b301',
          whiteSpace: 'nowrap',
        }}
      >
        ★
      </span>
    </span>
  );
}

function TrustShield() {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 78,
        height: 90,
        position: 'relative',
        margin: '0 auto 14px',
        filter: 'drop-shadow(0 10px 18px rgba(0,0,0,.22))',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          clipPath:
            'polygon(50% 0%, 92% 14%, 84% 68%, 50% 100%, 16% 68%, 8% 14%)',
          background:
            'linear-gradient(145deg, #f7d36a 0%, #d29b1f 55%, #9f6f0b 100%)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          inset: 7,
          clipPath:
            'polygon(50% 0%, 92% 14%, 84% 68%, 50% 100%, 16% 68%, 8% 14%)',
          background:
            'linear-gradient(145deg, #0b2747 0%, #123f6e 55%, #071a30 100%)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <div
          style={{
            width: 34,
            height: 28,
            borderRadius: 7,
            background: '#f7d36a',
            position: 'relative',
            boxShadow: 'inset 0 -3px 0 rgba(0,0,0,.12)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: 20,
              height: 16,
              border: '4px solid #f7d36a',
              borderBottom: 0,
              borderRadius: '12px 12px 0 0',
              left: 3,
              top: -15,
            }}
          />

          <div
            style={{
              position: 'absolute',
              width: 6,
              height: 10,
              borderRadius: 4,
              background: '#17395d',
              left: 14,
              top: 8,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function PublicRatingBadge({
  facilityId: facilityIdProp,
}) {
  const { t, i18n } = useTranslation();
  const params = useParams();

  const facilityId =
    facilityIdProp || params.facilityId || params.id;

  const [facility, setFacility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadFacilityRating() {
      if (!facilityId) {
        setError(t('publicRating.errors.missingFacility'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');

        const response = await fetch(
          `${API_BASE_URL}/public/note/${encodeURIComponent(facilityId)}`,
          {
            headers: {
              Accept: 'application/json',
            },
            signal: controller.signal,
          }
        );

        const payload = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(
            payload?.error ||
              t('publicRating.errors.loadFailed')
          );
        }

        const parsedNote = Number(payload?.note);
        const parsedReviewCount = Number(payload?.reviewCount);

        if (!Number.isFinite(parsedNote)) {
          throw new Error(
            t('publicRating.errors.invalidRating')
          );
        }

        setFacility({
          facilityId: payload?.facilityId,
          facilityName:
            typeof payload?.facilityName === 'string'
              ? payload.facilityName
              : '',
          facilityType: payload?.facilityType,
          city:
            typeof payload?.city === 'string'
              ? payload.city
              : '',
          note: clamp(parsedNote, 0, 5),
          reviewCount: Number.isFinite(parsedReviewCount)
            ? Math.max(0, parsedReviewCount)
            : 0,
        });
      } catch (err) {
        if (err?.name === 'AbortError') {
          return;
        }

        console.error('[PublicRatingBadge]', err);

        setError(
          err instanceof Error
            ? err.message
            : t('publicRating.errors.loadFailed')
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadFacilityRating();

    return () => controller.abort();
  }, [facilityId, t]);

  const note = facility?.note ?? null;

  const starFills = useMemo(() => {
    const current = note ?? 0;

    return Array.from({ length: 5 }, (_, index) =>
      clamp((current - index) * 100, 0, 100)
    );
  }, [note]);

  const formattedNote =
    note === null
      ? ''
      : new Intl.NumberFormat(i18n.language || 'en', {
          minimumFractionDigits: 1,
          maximumFractionDigits: 2,
        }).format(note);

  const formattedReviewCount = new Intl.NumberFormat(
    i18n.language || 'en'
  ).format(facility?.reviewCount ?? 0);

  const facilityTypeKey =
    FACILITY_TYPE_KEYS[facility?.facilityType] ||
    'other';

  const facilityTypeLabel = t(
    `showFacility.facilityType.${facilityTypeKey}`
  );

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background:
          'radial-gradient(circle at top left, #eef5ff 0%, #f8fbff 42%, #ffffff 100%)',
        fontFamily:
          'Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <article
        style={{
          width: 'min(520px, 100%)',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '1px solid #dce6f0',
          borderRadius: 28,
          boxShadow:
            '0 26px 80px rgba(15, 35, 60, 0.16)',
        }}
      >
        <header
          style={{
            padding: '30px 28px 26px',
            textAlign: 'center',
            background:
              'linear-gradient(145deg, #071a30 0%, #0b2f55 50%, #123f6e 100%)',
            color: '#ffffff',
          }}
        >
          <TrustShield />

          <div
            style={{
              fontSize: 28,
              fontWeight: 950,
              letterSpacing: '0.03em',
            }}
          >
            QRProof
          </div>

          <div
            style={{
              marginTop: 6,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '0.11em',
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,.72)',
            }}
          >
            {t('publicRating.verifiedReviews')}
          </div>
        </header>

        <section
          style={{
            padding: '34px 30px 32px',
            textAlign: 'center',
          }}
        >
          {loading && (
            <div
              role="status"
              style={{
                padding: '34px 0',
                color: '#486581',
                fontSize: 18,
                fontWeight: 800,
              }}
            >
              {t('publicRating.loading')}
            </div>
          )}

          {!loading && error && (
            <div
              role="alert"
              style={{
                padding: 18,
                border: '1px solid #f1b4b4',
                borderRadius: 14,
                color: '#9b1c1c',
                backgroundColor: '#fff5f5',
                fontWeight: 800,
              }}
            >
              {error}
            </div>
          )}

          {!loading && !error && facility && (
            <>
              <h1
                style={{
                  margin: 0,
                  color: '#102a43',
                  fontSize: 'clamp(26px, 6vw, 36px)',
                  lineHeight: 1.2,
                  fontWeight: 950,
                }}
              >
                {facility.facilityName ||
                  t('publicRating.unknownFacility')}
              </h1>

              {(facility.city || facilityTypeLabel) && (
                <div
                  style={{
                    marginTop: 12,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  {facility.city && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                        color: '#627d98',
                        fontSize: 15,
                        fontWeight: 700,
                      }}
                    >
                      <span aria-hidden="true">⌖</span>
                      {facility.city}
                    </span>
                  )}

                  {facility.city && facilityTypeLabel && (
                    <span
                      aria-hidden="true"
                      style={{ color: '#bcccdc' }}
                    >
                      •
                    </span>
                  )}

                  {facilityTypeLabel && (
                    <span
                      style={{
                        padding: '6px 12px',
                        color: '#1b4f7d',
                        backgroundColor: '#edf5ff',
                        border: '1px solid #c9def3',
                        borderRadius: 999,
                        fontSize: 13,
                        fontWeight: 800,
                      }}
                    >
                      {facilityTypeLabel}
                    </span>
                  )}
                </div>
              )}

              <div
                role="img"
                aria-label={t(
                  'publicRating.ariaRating',
                  {
                    note: formattedNote,
                  }
                )}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 5,
                  marginTop: 28,
                }}
              >
                {starFills.map(
                  (fillPercent, index) => (
                    <Star
                      key={index}
                      fillPercent={fillPercent}
                    />
                  )
                )}
              </div>

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'baseline',
                  gap: 6,
                  marginTop: 18,
                }}
              >
                <strong
                  style={{
                    color: '#102a43',
                    fontSize:
                      'clamp(50px, 12vw, 72px)',
                    lineHeight: 1,
                    fontWeight: 950,
                  }}
                >
                  {formattedNote}
                </strong>

                <span
                  style={{
                    color: '#829ab1',
                    fontSize: 22,
                    fontWeight: 800,
                  }}
                >
                  / 5
                </span>
              </div>

              <div
                style={{
                  marginTop: 10,
                  color: '#486581',
                  fontSize: 15,
                  fontWeight: 800,
                }}
              >
                {t('publicRating.reviewCount', {
                  count: facility.reviewCount,
                  formattedCount: formattedReviewCount,
                })}
              </div>

              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 9,
                  marginTop: 24,
                  padding: '10px 16px',
                  color: '#0b5d3b',
                  backgroundColor: '#e8f8ef',
                  border: '1px solid #a7dec0',
                  borderRadius: 999,
                  fontSize: 14,
                  fontWeight: 900,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 24,
                    height: 24,
                    color: '#ffffff',
                    backgroundColor: '#158552',
                    borderRadius: '50%',
                    fontSize: 15,
                  }}
                >
                  ✓
                </span>

                {t('publicRating.verifiedBy')}
              </div>

              <div
                style={{
                  marginTop: 26,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 10,
                }}
              >
                {[
                  {
                    icon: '⌖',
                    label: t(
                      'publicRating.proofs.geolocation'
                    ),
                  },
                  {
                    icon: '▦',
                    label: t(
                      'publicRating.proofs.qr'
                    ),
                  },
                  {
                    icon: '⌁',
                    label: t(
                      'publicRating.proofs.signature'
                    ),
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    style={{
                      padding: '12px 8px',
                      borderRadius: 14,
                      backgroundColor: '#f6f9fc',
                      border: '1px solid #e3ebf3',
                    }}
                  >
                    <div
                      style={{
                        color: '#1b4f7d',
                        fontSize: 24,
                        fontWeight: 900,
                      }}
                    >
                      {item.icon}
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        color: '#486581',
                        fontSize: 12,
                        lineHeight: 1.3,
                        fontWeight: 800,
                      }}
                    >
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>

              <p
                style={{
                  maxWidth: 390,
                  margin: '24px auto 0',
                  color: '#627d98',
                  fontSize: 14,
                  lineHeight: 1.65,
                }}
              >
                {t('publicRating.explanation')}
              </p>
            </>
          )}
        </section>

        <footer
          style={{
            padding: '15px 22px',
            textAlign: 'center',
            color: '#627d98',
            backgroundColor: '#f7f9fc',
            borderTop: '1px solid #e6ecf2',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {t('publicRating.footer')}
        </footer>
      </article>
    </main>
  );
}